import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { jobFunctions } from '@src/app/constants/jobFunctions';
import { useUser } from '@src/state/user';
import { useTIN } from '@src/state/taxInformation';
import { useOnloading } from '@src/state/landing';
import { useEditing } from '@src/state/editing/hooks';
import { useStandardUserRegistration } from '@src/state/SURegistration';
import { usePAARegistration } from '@src/state/PAARegistration';
import { JobView } from '../JobView';
import { getOrgType } from '../helpers';
import { config } from '@uhg-abyss/web/tools/config';
import { FLOW_ID } from '@src/common/AppConstants';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { RCMRedirectModal } from './RCMRedirectModal';

const MainContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  padding: '25px',
  marginBottom: '20px',
});

const BoxHeader = styled('div', {
  position: 'relative',
  marginBottom: '5px',
});

const BoxHeaderText = styled('h3', {
  fontWeight: 600,
  fontSize: '24px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const Description = styled('p', {
  fontSize: '16px',
  fontWeight: 500,
  marginBottom: '30px',
  fontFamily: '$primary',
});

const ClinicalStaffLabel = styled('p', {
  fontSize: '14px',
  fontWeight: 600,
  marginBottom: 0,
  fontFamily: '$primary',
  paddingBottom: '10px',
  paddingTop: '10px',
});

const BackButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  borderWidth: '2.5px',
  border: ' 2px solid $primary1',
  background: '$white',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const BackButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  marginLeft: '12px',
  fontSize: '16px',
});

const NextButton = styled('div', {
  float: 'right',
  width: '115px',
  padding: '6px',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$primary1',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const NextButtonText = styled('text', {
  color: '$white',
  fontWeight: 'bold',
  marginRight: '12px',
  fontSize: '16px',
});

const FormErrorMessage = styled('span', {
  fontWeight: 600,
  fontSize: '80%',
  width: '100%',
  color: 'red',
  display: 'inline-block',
  marginTop: '5px',
});

export const JobFunctionV2 = ({ handleBack, setJobFunctions, handleNext }) => {
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const uiMessages = lagoonContext.getTable('ui-messaging');
  const errorMessages = lagoonContext.getTable('error-messaging');
  const form = useForm();
  const [adminJobs, setAdminJobs] = useState([]);
  const [nonAdminJobs, setNonAdminJobs] = useState([]);
  const [formErrorMessage, setFormErrorMessage] = useState('');

  const [npiValueRequired, setNpiValueRequired] = useState(false);
  const [showCompanyOrVendorModal, setShowCompanyOrVendorModal] = useState(false);
  const [rcmError, setRcmError] = useState(null);
  const { TINNumber, TINInfo } = useTIN.state();
  const { notifyRCMTin } = useTIN.actions();
  const { currentFlow } = useOnloading.state();
  const { isEditing } = useEditing.state();
  const standardUser = useStandardUserRegistration.state();
  const primaryAdmin = usePAARegistration.state();

  const { userUUID, isAPIMarketPlaceUser } = useUser.state();

  useEffect(() => {
    setAdminJobs(
      jobFunctions.filter((j) => {
        return j.category === 'Administrative Staff';
      })
    );
    setNonAdminJobs(
      jobFunctions.filter((j) => {
        return j.category !== 'Administrative Staff';
      })
    );

    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      const orgType = getOrgType(currentFlow);
      let section1;
      let section2;
      let section3 = '';
      let pageName;
      if (!TINInfo?.hasPAA) {
        section1 = 'paa registration';
        section2 = 'job function';
        pageName = `digitalsecurity:${section1}:${section2}`;
      } else {
        section1 = 'tin registration';
        section2 = orgType;
        section3 = 'job function';
        pageName = `digitalsecurity:${section1}:${section2}:${section3}`;
      }
      adobeTaggingEvent('ON_PAGE_LOAD', {
        pageName,
        orgType,
        organization: TINInfo?.corpName,
        uuid: userUUID,
        tin: TINNumber,
        section1,
        section2,
        section3,
      });
    }

    if (
      primaryAdmin.selectedJobFunctions &&
      primaryAdmin.selectedJobFunctions.length > 0
    ) {
      populateSavedSelection(primaryAdmin.selectedJobFunctions[0]);
    }

    if (
      standardUser.selectedJobFunctions &&
      standardUser.selectedJobFunctions.length > 0
    ) {
      populateSavedSelection(standardUser.selectedJobFunctions[0]);
    }
  }, []);

  const populateSavedSelection = (jobs) => {
    const { RCMValue, companyName, npiValue, name, code } = jobs;
    jobFunctions.forEach((job) => {
      if (job.code === code) {
        form.setValue('SURegistration.jobFunction', name);
        if (RCMValue)
          form.setValue(`SURegistration.job_radio_${job.id}`, RCMValue);
        if (companyName)
          form.setValue(
            `SURegistration.job_company_txt_${job.id}`,
            companyName
          );
        if (npiValue)
          form.setValue(`SURegistration.job_npi_txt_${job.id}`, npiValue);
      }
    });
  };

  const onNext = () => {
    const jobFunction = form.getValues('SURegistration.jobFunction');
    if (!jobFunction) {
      if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
        const fields = ['Job Functions - no job function selected'];
        adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
      }
      return setFormErrorMessage('Please select at least one job function.');
    }
    form.validate(
      'SURegistration',
      (data) => {
        const job = Object.entries(data).reduce((acc, [key, value]) => {
          setFormErrorMessage('');

          if (value != null && value !== '') {
            if (key.includes('job_radio')) {
              acc['RCMValue'] = value;
            }
            if (key.includes('job_company_txt')) {
              acc['companyName'] = value;
            }
            if (key.includes('job_npi_txt')) {
              acc['npiValue'] = value;
            }
            if (key.includes('jobFunction')) {
              acc['name'] = value;
              acc['code'] = jobFunctions.find((val) => {
                return val.name == value;
              }).code;
            }
          }
          return acc;
        }, {});

        if (
          featureToggles.getRow('US9856248_RCM_REDIRECT')?.active &&
          currentFlow === FLOW_ID.PROVIDER &&
          job.RCMValue === 'Y'
        ) {
          setRcmError(null);
          notifyRCMTin(
            {
              url: uiMessages.getRow('REGISTER_API')?.message || config('REGISTER_API'),
              tin: TINNumber,
            },
            (response) => {
              if (response?.error) {
                const { code, description } = response.error;
                setRcmError(
                  (code && errorMessages.getRow(code)?.message) ||
                    description ||
                    errorMessages.getRow('DEFAULT')?.message
                );
                return;
              }
              setShowCompanyOrVendorModal(true);
            },
            () => {
              setRcmError(errorMessages.getRow('DEFAULT')?.message || 'An error occurred. Please try again.');
            }
          );
          return;
        }

        handleNext();
        setJobFunctions([job]);
      },
      (error) => {
        if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
          if (
            Object.keys(error).some((val) => {
              return val.includes('job_npi_txt');
            })
          ) {
            const fields = [`${jobFunction} NPI Field`];
            adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
          }

          const isRcmValidtionError = Object.keys(error).some((val) => {
            return val.includes('job_radio_');
          });
          if (isRcmValidtionError) {
            const fields = ['RCM Indicator not selected'];
            adobeTaggingEvent('FORM_VALIDATION_ERROR', {
              fields,
              pageName: `digitalsecurity:${
                !TINInfo?.hasPAA ? 'paa registration' : 'tin registration'
              }:job function`,
            });
          }
          const isRcmCompanyValidtionError = Object.keys(error).some((val) => {
            return val.includes('job_company_txt');
          });
          if (isRcmCompanyValidtionError) {
            const fields = ['RCM Company name missing'];
            adobeTaggingEvent('FORM_VALIDATION_ERROR', {
              fields,
              pageName: `digitalsecurity:${
                !TINInfo?.hasPAA ? 'paa registration' : 'tin registration'
              }:job function`,
            });
          }
        }
        return setFormErrorMessage('Please correct the above error(s).');
      }
    );
  };

  const onNpiValueChanged = (id, value) => {
    setFormErrorMessage('');
    setNpiValueRequired(false);
  };

  const clearSubValuesAndError = () => {
    setFormErrorMessage('');
    form.clearErrors();
    setNpiValueRequired(false);
    jobFunctions.map((val) => {
      form.setValue(`SURegistration.job_radio_${val.id}`, '');
      form.setValue(`SURegistration.job_company_txt_${val.id}`, '');
      form.setValue(`SURegistration.job_npi_txt_${val.id}`, '');
    });
  };

  return (
    <React.Fragment>
      <MainContainer>
          <BoxHeader>
            <BoxHeaderText>
            Select the job function that most closely aligns to your role.
          </BoxHeaderText>
        </BoxHeader>
        <Description>
          Please note: Your selection will not change your access or affect the
          functionality of the portal.
        </Description>
        <FormProvider state={form}>
          <RadioGroup
            model="SURegistration.jobFunction"
            label="Job Function"
            hideLabel
            onChange={clearSubValuesAndError}
            validators={{ required: true }}
          >
            {adminJobs.map((job) => {
              return (
                <JobView
                  key={job.id}
                  {...job}
                  onNpiChanged={onNpiValueChanged}
                  npiValueRequired={npiValueRequired}
                  isAPIMarketPlaceUser={isAPIMarketPlaceUser}
                />
              );
            })}
            {nonAdminJobs.length >= 1 ? (
              <ClinicalStaffLabel>Clinical Staff</ClinicalStaffLabel>
            ) : null}
            {nonAdminJobs.map((job) => {
              return (
                <JobView
                  key={job.id}
                  {...job}
                  onNpiChanged={onNpiValueChanged}
                  npiValueRequired={npiValueRequired}
                />
              );
            })}
          </RadioGroup>
        </FormProvider>
        {formErrorMessage ? (
          <FormErrorMessage>{formErrorMessage}</FormErrorMessage>
        ) : null}
        {rcmError ? (
          <Alert
            variant="error"
            title="Error"
            onClose={() => setRcmError(null)}
          >
            {rcmError}
          </Alert>
        ) : null}
      </MainContainer>
      <Flex
        css={{
          'abyss-flex-root': {
            justifyContent: 'space-between',
          },
        }}
      >
        <Flex.Content space-50>
          {!isEditing && (
            <BackButton onClick={handleBack}>
              <IconSymbol icon="arrow_back" size={20} color="$primary1" />
              <BackButtonText>Back</BackButtonText>
            </BackButton>
          )}
        </Flex.Content>
        <Flex.Content space-50>
          <NextButton onClick={onNext}>
            <NextButtonText>{isEditing ? 'Save' : 'Next'}</NextButtonText>
            <IconSymbol icon="arrow_forward" size={16} color="$primary2" />
          </NextButton>
        </Flex.Content>
      </Flex>
      <RCMRedirectModal
        isOpen={showCompanyOrVendorModal}
      />
    </React.Fragment>
  );
};

JobFunctionV2.propTypes = {
  handleBack: PropTypes.func.isRequired,
  setJobFunctions: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
};
